import { formatDeleteHeaders, formatGetHeaders, formatPostHeaders } from '@configurations/api-configuration';
import { RegionManagerGateway } from '@scenario/gateways/region-manager.gateway';
import { RegionManager } from '@scenario/models/region-manager.model';
import { environment } from '../../../../../environments/environment';
import { RegionManagerEntity } from './region-manager.entity';
import { RegionManagerMapper } from './region-manager.mapper';

export class ApiRegionManagerGateway implements RegionManagerGateway {
  private readonly regionManagerApi = `${environment.backend_url}/api/v1/regionmanager`;
  private readonly mapper = new RegionManagerMapper();

  async retrieveAll(): Promise<RegionManager[]> {
    const response = await fetch(this.regionManagerApi, await formatGetHeaders());
    if (!response.ok) {
      throw new Error(`Failed to retrieve region managers: ${response.status}`);
    }
    const entities: RegionManagerEntity[] = await response.json();
    return this.mapper.mapArrayToModel(entities);
  }

  async create(regionManager: RegionManager): Promise<RegionManager> {
    const response = await fetch(
      this.regionManagerApi,
      await formatPostHeaders(this.mapper.mapToEntity(regionManager)),
    );
    if (response.status !== 201) {
      throw new Error(await this.toCreateErrorMessage(response));
    }
    return this.mapper.mapToModel(await response.json());
  }

  async remove(id: number): Promise<RegionManager> {
    const response = await fetch(`${this.regionManagerApi}/${id}`, await formatDeleteHeaders());
    if (!response.ok) {
      throw new Error(`Failed to remove region manager: ${response.status}`);
    }
    return this.mapper.mapToModel(await response.json());
  }

  private async toCreateErrorMessage(response: Response): Promise<string> {
    if (response.status === 409) {
      return 'An active region manager already exists for this source and market area.';
    }
    return `Failed to create region manager: ${response.status}`;
  }
}
